/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router-dom";

import { find } from "lodash";

import { CommandBar, Label, Stack, StackItem, Text } from "@fluentui/react";

import { useAuth } from "Authentication/AuthProvider";
import {
  bodyContainerStackStyles,
  headerStackStyles,
  headerTextStyles,
  ContainerWrapper,
} from "Components/Common/SharedTableStyles.styles";
import { SetProfileToPrivateSidePanel } from "Components/SetProfileToPrivateSidePanel/SetProfileToPrivateSidePanel";
import StudentEditProfileEnhancedForm from "Components/StudentEditProfile/StudentEditProfileEnhancedForm";
import { INVITED_STATUS } from "Constants/FormStatusConstants";
import { OperationStatus } from "Constants/OperationStatus";
import { namesLocalized } from "Constants/ProfileConstants";
import { ACTIVE } from "Constants/ProgramProfileConstants";
import {
  getAppStateCountryRegionsOptionList,
  getAppStateCountryRegionLoadError,
  getAppStateCountryRegionPhoneOptionList,
} from "Dataflows/CountryRegion/CountryRegionSelector";
import { getProgramLanguage } from "Dataflows/ProgramLanguage/ProgramLanguageAction";
import {
  getAppStateProgramLanguageLoadError,
  getAppStateProgramLanguageOptionList,
} from "Dataflows/ProgramLanguage/ProgramLanguageSelector";
import {
  getAppStateSchoolLoadError,
  getAppStateSchoolOptionList,
} from "Dataflows/School/SchoolSelector";
import { getSocialNetwork } from "Dataflows/SocialNetwork/SocialNetworkAction";
import {
  getAppStateSocialNetworkLoadError,
  getAppStateSocialNetworkOptionList,
} from "Dataflows/SocialNetwork/SocialNetworkSelector";
import {
  getAppStateProvincesOptionList,
  getAppStateStateProvinceLoadError,
  getAppStateStateProvinceSchoolLoadError,
  getAppStateStateProvinceSchoolOptionList,
  getAppStateStateProvinceShippingLoadError,
  getAppStateStateProvinceShippingOptionList,
} from "Dataflows/StateProvince/StateProvinceSelector";
import {
  loadUserProfile,
  updateUserProfile,
  userProfileIsPrivateUpdate,
} from "Dataflows/UserProfile/UserProfileAction";
import {
  getAppStateUpdateUserProfileError,
  getAppStateUserProfile,
  getUserProfileUpdateIsPrivateError,
  getUserProfileUpdateIsPrivateOperationStatus,
  isAppStateCurrentUserProfileLoading,
  isAppStateSelectedUserProfileUpdating,
} from "Dataflows/UserProfile/UserProfileSelector";
import { IUserProfile } from "Interfaces/UserProfile/IUserProfile";
import { formatDate } from "Utility/FormatHelper";

import {
  bodyColumnStackItemStyles,
  bodyContainerStackTokens,
  bodyRowStackStyles,
  commandBarStyles,
  labelStyles,
  subHeaderTextStyles,
} from "./StudentProfile.styles";

/**
 * The Most Valuable Professional Profile component
 * @method
 * @returns {ReactElement} The Most Valuable Professional Profile Details component
 */
const StudentProfile = (): ReactElement => {
  const { isUserLoggedIn, loading, hasAnyRoles } = useAuth();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isSetToPrivatePanelOpen, setIsSetToPrivatePanelOpen] = useState(false);
  const profileInfo = useSelector(getAppStateUserProfile);
  const dispatch = useDispatch();

  // Below needed for edit profile form
  const countryRegionOptionList = useSelector(getAppStateCountryRegionsOptionList);
  const countryRegionPhoneOptionList = useSelector(getAppStateCountryRegionPhoneOptionList);
  const countryRegionsLoadError = useSelector(getAppStateCountryRegionLoadError);
  const stateProvinceOptionList = useSelector(getAppStateProvincesOptionList);
  const stateProvinceShippingOptionList = useSelector(getAppStateStateProvinceShippingOptionList);
  const stateProvinceSchoolOptionList = useSelector(getAppStateStateProvinceSchoolOptionList);
  const stateProvincesLoadError = useSelector(getAppStateStateProvinceLoadError);
  const stateProvincesShippingLoadError = useSelector(getAppStateStateProvinceShippingLoadError);
  const stateProvincesSchoolLoadError = useSelector(getAppStateStateProvinceSchoolLoadError);
  const schoolOptionList = useSelector(getAppStateSchoolOptionList);
  const schoolsDataLoadError = useSelector(getAppStateSchoolLoadError);
  const socialNetworkOptionList = useSelector(getAppStateSocialNetworkOptionList);
  const socialNetworksDataLoadError = useSelector(getAppStateSocialNetworkLoadError);
  const programLanguageOptionList = useSelector(getAppStateProgramLanguageOptionList);
  const programLanguageDataLoadError = useSelector(getAppStateProgramLanguageLoadError);
  const isCurrentUserProfileLoading = useSelector(isAppStateCurrentUserProfileLoading);
  const updateProfileError = useSelector(getAppStateUpdateUserProfileError);
  const isSelectedUserProfileUpdating = useSelector(isAppStateSelectedUserProfileUpdating);
  const userProfileIsPrivateUpdateStatus = useSelector(
    getUserProfileUpdateIsPrivateOperationStatus
  );
  const userProfileIsPrivateUpdateError = useSelector(getUserProfileUpdateIsPrivateError);

  useEffect(() => {
    if (userProfileIsPrivateUpdateStatus === OperationStatus.Succeeded && profileInfo?.id) {
      setIsSetToPrivatePanelOpen(false);
      dispatch(loadUserProfile(profileInfo.id));
    }
  }, [userProfileIsPrivateUpdateStatus]);

  useEffect(() => {
    dispatch(getSocialNetwork());
    dispatch(getProgramLanguage());
  }, []);

  const canEditUser =
    profileInfo.programProfileStatusName === ACTIVE &&
    profileInfo.application?.applicationStatus === INVITED_STATUS;

  if (isUserLoggedIn && !hasAnyRoles && !loading) {
    return <Redirect to="/unauthorized" />;
  }

  const editProfileInformationClicked = (): void => {
    setIsPanelOpen(true);
  };

  const handleUpdateUserProfile = (userProfile: IUserProfile) => {
    dispatch(updateUserProfile(userProfile));
  };

  const handlePanelClose = () => {
    setIsPanelOpen(false);
  };

  const handleSetProfileToPrivateTogglePanel = () => {
    if (profileInfo && profileInfo.id) {
      setIsSetToPrivatePanelOpen(!isSetToPrivatePanelOpen);
    }
  };

  const handleSetProfileToPrivateSave = () => {
    if (profileInfo && profileInfo.id) {
      dispatch(userProfileIsPrivateUpdate(!profileInfo.isPrivate, profileInfo.id));
    }
  };

  const commandBarItems = [
    {
      key: "editProgramInformation",
      text: "Edit Profile Details",
      iconProps: { iconName: "Edit" },
      onClick: (): void => {
        editProfileInformationClicked();
      },
    },
    {
      key: "setProfileToPrivate",
      text: `Set Profile to ${profileInfo?.isPrivate ? "Public" : "Private"}`,
      iconProps: { iconName: `${profileInfo?.isPrivate ? "UserFollowed" : "UserRemove"}` },
      onClick: (): void => {
        handleSetProfileToPrivateTogglePanel();
      },
    },
  ];

  return (
    <>
      <Stack tokens={{ childrenGap: "1.5rem" }}>
        <Stack>
          {canEditUser && (
            <CommandBar
              items={commandBarItems}
              ariaLabel="Edit user actions"
              styles={commandBarStyles}
            />
          )}
          <ContainerWrapper>
            <Stack styles={headerStackStyles}>
              <Text styles={headerTextStyles}>Personal Information</Text>
            </Stack>
            <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>First Name</Label>
                  <Text>{profileInfo?.firstName}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Last Name</Label>
                  <Text>{profileInfo?.lastName}</Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Localized First Name</Label>
                  <Text>{profileInfo?.localizedFirstName}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Localized Last Name</Label>
                  <Text>{profileInfo?.localizedLastName}</Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Print Name</Label>
                  <Text>
                    {find(namesLocalized, (o) => o.key === !!profileInfo?.awardNameLocalized)?.text}
                  </Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Display Name</Label>
                  <Text>
                    {
                      find(namesLocalized, (o) => o.key === !!profileInfo?.screenNameLocalized)
                        ?.text
                    }
                  </Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Title</Label>
                  <Text>{profileInfo?.titleName}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Date of birth</Label>
                  {!!profileInfo?.dateOfBirth ? (
                    <Text>{formatDate(new Date(profileInfo?.dateOfBirth))}</Text>
                  ) : (
                    <></>
                  )}
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Site Email</Label>
                  <Text>{profileInfo?.primaryEmail}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Preferred Email</Label>
                  <Text>{profileInfo?.secondaryEmail}</Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Gender</Label>
                  <Text>{profileInfo?.genderName}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Pronoun</Label>
                  <Text>{profileInfo?.genderPronounPreferenceName}</Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Student Partner Email</Label>
                  <Text>{profileInfo?.studentPartnerEmail}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Languages</Label>
                  <Text>{profileInfo?.languages?.join(", ")}</Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Shirt Fit Type</Label>
                  <Text>{profileInfo?.shirtFitType}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Shirt Size</Label>
                  <Text>{profileInfo?.shirtSize}</Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Preferred Programming Language</Label>
                  <Text>{profileInfo?.userProfileProgramLanguage?.join(", ")}</Text>
                </StackItem>
              </Stack>
            </Stack>
          </ContainerWrapper>
        </Stack>
        <ContainerWrapper>
          <Stack>
            <Stack styles={headerStackStyles}>
              <Text styles={headerTextStyles}>Biography</Text>
            </Stack>
            <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Headline</Label>
                  <Text>{profileInfo?.headline}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Biography</Label>
                  <Text>{profileInfo?.biography}</Text>
                </StackItem>
              </Stack>
            </Stack>
          </Stack>
        </ContainerWrapper>
        <ContainerWrapper>
          <Stack>
            <Stack styles={headerStackStyles}>
              <Text styles={headerTextStyles}>Address</Text>
            </Stack>
            <Stack>
              <Text styles={subHeaderTextStyles}>Primary Address</Text>
            </Stack>
            <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Country Code</Label>
                  <Text>{profileInfo?.primaryPhoneCountryOrRegionId}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Phone number</Label>
                  <Text>{profileInfo?.primaryPhone}</Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Address Line 1</Label>
                  <Text>{profileInfo?.address1}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Address Line 2</Label>
                  <Text>{profileInfo?.address2}</Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>City</Label>
                  <Text>{profileInfo?.addressCity}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>State/Province</Label>
                  <Text>{profileInfo?.addressStateOrProvince}</Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Post code</Label>
                  <Text>{profileInfo?.addressPostCode}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Country Region</Label>
                  <Text>{profileInfo?.addressCountryOrRegionName}</Text>
                </StackItem>
              </Stack>
            </Stack>
            {!profileInfo?.shippingSameAsAddress ? (
              <>
                <Stack>
                  <Text styles={subHeaderTextStyles}>Shipping Address</Text>
                </Stack>
                <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
                  <Stack horizontal styles={bodyRowStackStyles}>
                    <StackItem styles={bodyColumnStackItemStyles}>
                      <Label styles={labelStyles}>Phone number</Label>
                      <Text>{profileInfo?.shippingPhoneNumber}</Text>
                    </StackItem>
                  </Stack>
                  <Stack horizontal styles={bodyRowStackStyles}>
                    <StackItem styles={bodyColumnStackItemStyles}>
                      <Label styles={labelStyles}>Address Line 1</Label>
                      <Text>{profileInfo?.shippingAddress1}</Text>
                    </StackItem>
                    <StackItem styles={bodyColumnStackItemStyles}>
                      <Label styles={labelStyles}>Address Line 2</Label>
                      <Text>{profileInfo?.shippingAddress2}</Text>
                    </StackItem>
                  </Stack>
                  <Stack horizontal styles={bodyRowStackStyles}>
                    <StackItem styles={bodyColumnStackItemStyles}>
                      <Label styles={labelStyles}>City</Label>
                      <Text>{profileInfo?.shippingCity}</Text>
                    </StackItem>
                    <StackItem styles={bodyColumnStackItemStyles}>
                      <Label styles={labelStyles}>State/Province</Label>
                      <Text>{profileInfo?.shippingStateOrProvince}</Text>
                    </StackItem>
                  </Stack>

                  <Stack horizontal styles={bodyRowStackStyles}>
                    <StackItem styles={bodyColumnStackItemStyles}>
                      <Label styles={labelStyles}>Post code</Label>
                      <Text>{profileInfo?.shippingPostCode}</Text>
                    </StackItem>
                    <StackItem styles={bodyColumnStackItemStyles}>
                      <Label styles={labelStyles}>Country Region</Label>
                      <Text>{profileInfo?.shippingCountryOrRegionName}</Text>
                    </StackItem>
                  </Stack>
                </Stack>
              </>
            ) : (
              <></>
            )}
          </Stack>
        </ContainerWrapper>
        <ContainerWrapper>
          <Stack>
            <Stack styles={headerStackStyles}>
              <Text styles={headerTextStyles}>Academic Information</Text>
            </Stack>
            <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
              {profileInfo?.userProfileSchool.map((school) => (
                <Stack key={school.id}>
                  <Stack>
                    <Text styles={subHeaderTextStyles}>Profile School ID: {school.id}</Text>
                  </Stack>
                  <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
                    <Stack horizontal styles={bodyRowStackStyles}>
                      <StackItem styles={bodyColumnStackItemStyles}>
                        <Label styles={labelStyles}>Country Region</Label>
                        <Text>{school.schoolCountryRegionName}</Text>
                      </StackItem>
                      <StackItem styles={bodyColumnStackItemStyles}>
                        <Label styles={labelStyles}>State/Province</Label>
                        <Text>{school.schoolStateProvinceName}</Text>
                      </StackItem>
                    </Stack>
                    <Stack horizontal styles={bodyRowStackStyles}>
                      <StackItem styles={bodyColumnStackItemStyles}>
                        <Label styles={labelStyles}>School Name</Label>
                        <Text>{school.schoolName}</Text>
                      </StackItem>
                      <StackItem styles={bodyColumnStackItemStyles}>
                        <Label styles={labelStyles}>Degree Program</Label>
                        <Text>{school.schoolDegreeProgramName}</Text>
                      </StackItem>
                    </Stack>
                    <Stack horizontal styles={bodyRowStackStyles}>
                      <StackItem styles={bodyColumnStackItemStyles}>
                        <Label styles={labelStyles}>Degree Level Name</Label>
                        <Text>{school.schoolDegreeLevelName}</Text>
                      </StackItem>
                      <StackItem styles={bodyColumnStackItemStyles}>
                        <Label styles={labelStyles}>Expected Graduation Date</Label>
                        <Text>
                          {!!school.expectedGraduationDate
                            ? formatDate(new Date(school.expectedGraduationDate))
                            : ""}
                        </Text>
                      </StackItem>
                    </Stack>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </ContainerWrapper>
        <ContainerWrapper>
          <Stack>
            <Stack styles={headerStackStyles}>
              <Text styles={headerTextStyles}>Social Media</Text>
            </Stack>
            <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
              {profileInfo?.userProfileSocialNetwork.map((x) => (
                <Stack key={x.id} horizontal styles={bodyRowStackStyles}>
                  <StackItem styles={bodyColumnStackItemStyles}>
                    <Label styles={labelStyles}>Network</Label>
                    <Text>{x?.socialNetworkName}</Text>
                  </StackItem>
                  <StackItem styles={bodyColumnStackItemStyles}>
                    <Label styles={labelStyles}>Link/Url</Label>
                    <Text>{x?.socialNetworkImageLink}</Text>
                  </StackItem>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </ContainerWrapper>
      </Stack>

      {isPanelOpen && (
        <StudentEditProfileEnhancedForm
          selectedUserProfile={profileInfo}
          isSelectedUserProfileLoading={isCurrentUserProfileLoading}
          updateUserProfile={(userProfile) => handleUpdateUserProfile(userProfile)}
          updateProfileError={updateProfileError}
          isSelectedUserProfileUpdating={isSelectedUserProfileUpdating}
          countryRegionOptionList={countryRegionOptionList}
          countryRegionPhoneOptionList={countryRegionPhoneOptionList}
          stateProvinceOptionList={stateProvinceOptionList}
          stateProvinceShippingOptionList={stateProvinceShippingOptionList}
          stateProvinceSchoolOptionList={stateProvinceSchoolOptionList}
          schoolOptionList={schoolOptionList}
          schoolOptionsSelected={[]} // schoolOptionsSelected
          countryRegionsLoadError={countryRegionsLoadError}
          stateProvincesLoadError={stateProvincesLoadError}
          stateProvincesShippingLoadError={stateProvincesShippingLoadError}
          stateProvincesSchoolLoadError={stateProvincesSchoolLoadError}
          schoolsDataLoadError={schoolsDataLoadError}
          socialNetworkOptionList={socialNetworkOptionList}
          programLanguageOptionList={programLanguageOptionList}
          socialNetworksDataLoadError={socialNetworksDataLoadError}
          programLanguageDataLoadError={programLanguageDataLoadError}
          canEditUser={canEditUser}
          isPanelOpen={isPanelOpen}
          handlePanelClose={handlePanelClose}
        />
      )}

      <SetProfileToPrivateSidePanel
        userProfileId={profileInfo.id}
        isPrivate={profileInfo.isPrivate ?? false}
        isOpen={isSetToPrivatePanelOpen}
        updateStatus={userProfileIsPrivateUpdateStatus}
        updateError={userProfileIsPrivateUpdateError}
        onDismiss={handleSetProfileToPrivateTogglePanel}
        onSave={handleSetProfileToPrivateSave}
      />
    </>
  );
};

export default StudentProfile;
